import { downloadFile } from '@/services/helpers';
import { Box, Button, toast } from '@palmetto/palmetto-components';
import { Form, Formik } from 'formik';

import { debounceAsync } from '../../helpers/debounceAsync';
import { useRunTaskMutation } from '../../services/adminTasks';
import { useLazySearchOrganizationsQuery } from '../../services/organizations';
import { AsyncSelectInput } from '../Forms/AsyncSelect';
import { sortByLabel } from '../Pricing/Mappings/util';

export const OrganizationUserExport = () => {
  const [runTask] = useRunTaskMutation();
  const [triggerSearch] = useLazySearchOrganizationsQuery();

  const handleSearchText = async (searchText: string) => {
    const results = await triggerSearch({
      searchTerm: searchText,
      useRootOrganization: true,
    });

    return results?.data?.flat
      ?.map((org) => ({
        value: org.alias,
        label: `${org.name} (${org.alias})`,
      }))
      .toSorted(sortByLabel);
  };
  const debouncedSearch = debounceAsync(handleSearchText, 400);

  const onSubmit = async (values: any) => {
    const toastId = toast.loading('Exporting organization users');
    try {
      const blob = await runTask({
        task: 'organizationUserExport',
        orgAlias: values.selectedOrg.value,
      }).unwrap();

      const fileName = `organization-users-${values.selectedOrg.value}.csv`;
      const url = window.URL.createObjectURL(blob);
      await downloadFile(url, fileName);

      toast.success('Export completed successfully');
    } catch (e: any) {
      console.error(e);
      toast.error(e?.data?.message || 'Error exporting users');
    } finally {
      toast.dismiss(toastId);
    }
  };

  return (
    <Box padding="xl">
      <Formik
        initialValues={{
          selectedOrg: '',
        }}
        onSubmit={onSubmit}
      >
        {({ values, isSubmitting, setFieldValue, handleBlur, touched, errors }) => (
          <Form>
            <Box gap="md">
              <Box childGap="lg">
                <AsyncSelectInput
                  id="selectedOrg"
                  name="selectedOrg"
                  label="Select an organization to export all users"
                  loadOptions={debouncedSearch}
                  value={values.selectedOrg}
                  onChange={(event) => {
                    setFieldValue('selectedOrg', event.target.value);
                  }}
                  onBlur={handleBlur}
                  isClearable
                  isDisabled={isSubmitting}
                  noOptionsMessage={() => 'Search for an organization'}
                  error={touched.selectedOrg && (errors.selectedOrg as string)}
                />
              </Box>
              <Box>
                <Button size="md" variant="primary" type="submit" isDisabled={!values.selectedOrg || isSubmitting}>
                  Export
                </Button>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
